import { useState, Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate } from 'react-router-dom';

import {
    Caption,
    FilledCard,
    Heading1,
    Heading2,
    Heading3,
    HorizontalDivider,
    IconArrowDownward,
    SelectionCard,
    Stack,
    Subtitle2,
} from '@myphx-engineering/phx-design-system';

import { useMedicationConfigOverride } from '../../../contexts/DrugProgramProvider';
import {
    useFillPathway,
    useFillPrescription,
} from '../../../contexts/fill/use-fill-prescription';
import type {
    NdcSwitchTargetV2,
    SwitchRequestSource,
} from '../../../types/drug-config';
import { getMedicationSwitchConfig } from '../../../util/config/switch-config';
import { getAbsoluteRoute } from '../../../util/get-absolute-route';
import { ConditionalComponent } from '../../utility/ConditionalComponent';

import { BulletSection } from './switch-prescription/BulletSection';
import { ContentSection } from './switch-prescription/ContentSection';
import { DrugCardInfo } from './switch-prescription/DrugCardInfo';
import { KeepCurrentButton } from './switch-prescription/KeepCurrentButton';
import { SwitchRequestButton } from './switch-prescription/SwitchRequestButton';

const SingleTargetCard = ({
    source,
    target,
}: {
    source: SwitchRequestSource;
    target: NdcSwitchTargetV2;
}) => {
    const { t } = useTranslation();

    return (
        <FilledCard>
            <Stack gap="sm">
                <Stack
                    gap="xs"
                    data-testid="switch-options-current-prescription"
                >
                    <Caption color="primary" tt="uppercase">
                        {t('fill.switch.cards.current.heading')}
                    </Caption>
                    <DrugCardInfo info={source} />
                </Stack>
                <HorizontalDivider
                    color="primary"
                    label={
                        <IconArrowDownward
                            color="primary"
                            height="2rem"
                            width="2rem"
                        />
                    }
                />
                <Stack
                    gap="xs"
                    data-testid={`switch-options-new-form-option-${target.ndc}`}
                >
                    <Caption color="primary" tt="uppercase">
                        {t('fill.switch.cards.target.heading')}
                    </Caption>
                    <DrugCardInfo info={target} />
                </Stack>
            </Stack>
        </FilledCard>
    );
};

const MultiTargetCard = ({
    source,
    targets,
    selectedTarget,
    setSelectedTarget,
}: {
    source: SwitchRequestSource;
    targets: NdcSwitchTargetV2[];
    selectedTarget: NdcSwitchTargetV2 | null;
    setSelectedTarget: (t: NdcSwitchTargetV2) => void;
}) => {
    const { t } = useTranslation();

    return (
        <>
            <FilledCard data-testid="switch-options-current-prescription">
                <Stack gap="xs">
                    <Caption color="primary" tt="uppercase">
                        {t('fill.switch.cards.current.heading')}
                    </Caption>
                    <DrugCardInfo info={source} />
                </Stack>
            </FilledCard>
            <HorizontalDivider
                color="primary"
                label={
                    <IconArrowDownward
                        color="primary"
                        height="2rem"
                        width="2rem"
                    />
                }
            />
            <Subtitle2>{t('fill.switch.cards.selectNewForm')}</Subtitle2>

            <Stack gap="sm">
                {targets.map((target, idx) => {
                    const isSelected = selectedTarget?.ndc === target.ndc;
                    const isLast = idx === targets.length - 1;

                    return (
                        <Fragment key={target.ndc}>
                            <SelectionCard
                                isSelected={isSelected}
                                color="primary"
                                onClick={() => setSelectedTarget(target)}
                                data-testid={`switch-options-new-form-option-${target.ndc}`}
                            >
                                <Stack gap="xs">
                                    <DrugCardInfo
                                        info={target}
                                        iconBgColor="primary"
                                    />
                                </Stack>
                            </SelectionCard>
                            {isLast ? null : (
                                <HorizontalDivider
                                    label={
                                        <Heading3>
                                            {t('fill.switch.cards.or')}
                                        </Heading3>
                                    }
                                />
                            )}
                        </Fragment>
                    );
                })}
            </Stack>
        </>
    );
};

export const SwitchPrescriptionScreen = () => {
    const { t } = useTranslation();
    const { drxId, medication } = useFillPrescription();
    const [isSwitchLoading, setIsSwitchLoading] = useState(false);

    const { pathway, subPathway } = useFillPathway();
    const medConfig = useMedicationConfigOverride();

    const switchConfig = getMedicationSwitchConfig(
        medConfig,
        medication,
        pathway,
        subPathway
    );

    const [selectedTarget, setSelectedTarget] =
        useState<NdcSwitchTargetV2 | null>(
            switchConfig?.targets.length === 1 ? switchConfig.targets[0] : null
        );

    if (!switchConfig || switchConfig.targets.length === 0) {
        return (
            <Navigate
                replace
                to={{
                    pathname: getAbsoluteRoute('fill.drxId.root', { drxId }),
                }}
            />
        );
    }

    const { whatYouGet } = switchConfig.content.details;

    return (
        <Stack gap="lg" data-testid="switch-options-page-root">
            <Stack gap="xs" data-testid="switch-options-title-and-intro">
                <Heading1>{switchConfig.content.pageTitle}</Heading1>
                <ContentSection
                    content={switchConfig.content.pageDescription}
                />
            </Stack>

            <ConditionalComponent
                condition={
                    switchConfig.targets.length === 1 && !!selectedTarget
                }
            >
                <SingleTargetCard
                    target={selectedTarget!}
                    source={switchConfig.source}
                />
            </ConditionalComponent>
            <ConditionalComponent condition={switchConfig.targets.length > 1}>
                <MultiTargetCard
                    source={switchConfig.source}
                    targets={switchConfig.targets}
                    selectedTarget={selectedTarget}
                    setSelectedTarget={setSelectedTarget}
                />
            </ConditionalComponent>

            <Stack gap="xs">
                <Heading2>{t('fill.switch.headings.why')}</Heading2>
                <ContentSection
                    content={switchConfig.content.details.whySwitch}
                />
            </Stack>

            <Stack gap="xs">
                <Heading3>{t('fill.switch.headings.whatYouGet')}</Heading3>
                <Stack>
                    {whatYouGet.content && (
                        <ContentSection content={whatYouGet.content} />
                    )}
                    {whatYouGet.bullets && (
                        <BulletSection bullets={whatYouGet.bullets} />
                    )}
                </Stack>
            </Stack>

            <Stack gap="xs">
                <Heading3>{t('fill.switch.headings.whatsNext')}</Heading3>
                <ContentSection
                    content={switchConfig.content.details.whatsNext}
                />
            </Stack>

            <Stack>
                <SwitchRequestButton
                    drxId={drxId}
                    onLoadingChange={setIsSwitchLoading}
                    requestSource={switchConfig.source}
                    switchTarget={selectedTarget}
                    data-testid="switch-options-request-switch-button"
                />
                <KeepCurrentButton
                    disabled={isSwitchLoading}
                    drxId={drxId}
                    requestSource={switchConfig.source}
                />
            </Stack>
        </Stack>
    );
};
